(function () {
  /**
   * Payment section — gateway chips + gateway-specific instruction banner +
   * wallet-due preview (informational only, not a selectable gateway).
   * Mirrors checkout_payment_section.dart + PreviewWalletAmountDueUseCase
   * (lib/features/wallet/domain/usecases/preview_wallet_amount_due_use_case.dart)
   * exactly.
   */

  var STR = (window.ErosAccountStrings && window.ErosAccountStrings.checkout) || {};
  function t(key, fallback) {
    return STR[key] || fallback;
  }

  // shippingIqd is now the caller's resolved figure (0 for pickup, else the
  // live-fetched/fallback delivery-or-gift rate — see eros-checkout.js's
  // mountPayment()) rather than a hardcoded constant computed in here from
  // isPickup alone: the owner changes these Shopify delivery rates himself,
  // with no code deploy, so this preview must track whichever fee the
  // order summary is currently showing, not a fixed 3000.
  function previewWalletDue(balanceIqd, merchandiseTotal, shippingIqd) {
    var shopifyTotalIqd = Math.round(merchandiseTotal + shippingIqd);
    var tagValueIqd = 0;
    if (balanceIqd > 0) tagValueIqd = Math.min(balanceIqd, shopifyTotalIqd);
    else if (balanceIqd < 0) tagValueIqd = balanceIqd;
    var amountDueIqd = shopifyTotalIqd - tagValueIqd;
    if (amountDueIqd < 0) amountDueIqd = 0;
    return { shopifyTotalIqd: shopifyTotalIqd, amountDueIqd: amountDueIqd };
  }

  /**
   * @param {HTMLElement} container gateway chips + hint
   * @param {HTMLElement} walletHost wallet balance/amount-due rows — rendered
   *   as a distinct block, separate from the gateway chips (matches
   *   CheckoutWalletDueSection being its own widget below CheckoutOrderSummary,
   *   not part of the payment section)
   * @param {number} merchandiseTotal cart subtotal (no shipping), IQD
   * @param {number} shippingIqd resolved shipping fee for the wallet-due
   *   preview — 0 for pickup, else the caller's selected delivery/gift
   *   rate (live-fetched or fallback). See eros-checkout.js's mountPayment().
   * @param {boolean} isGift when true, the cod gateway chip is filtered out
   *   entirely — the customer must never see cash-on-delivery while in gift
   *   mode (mirrors checkout_modal_sheet.dart's Flutter-side filtering).
   * @param {function()} onChange called when the selected gateway changes
   * @param {?string} [preferredGatewayName] keep this chip selected across remounts
   * @param {{ allowedChipIds?: string[] }} [options] ADR-200: a gift-credit
   *   cart shows only the chips GET /v1/gift-credit/config allows (the server
   *   refuses any other with gift_credit_payment_method).
   * @returns {{ getSelectedGatewayName: function(): ?string }}
   */
  function mount(container, walletHost, merchandiseTotal, shippingIqd, isGift, onChange, preferredGatewayName, options) {
    var allowedChipIds = options && Array.isArray(options.allowedChipIds) ? options.allowedChipIds : null;
    var selectedGatewayName = null;
    var gateways = [];
    // A later mount() call must not answer the customer's own selection
    // with the default — stale results from an EARLIER mount are also
    // ignored so the chips on screen are always the ones read at confirm.
    var stale = false;
    if (container.__erosPaymentCancel) container.__erosPaymentCancel();
    container.__erosPaymentCancel = function () { stale = true; };

    container.innerHTML = '';
    if (walletHost) walletHost.innerHTML = '';
    var loading = window.ErosUI.el('p');
    loading.textContent = t('loading', 'جارٍ التحميل...');
    container.appendChild(loading);

    Promise.all([window.ErosCommerce.paymentGateways(), window.ErosCommerce.getProfile().catch(function () { return null; })]).then(function (results) {
      gateways = (results[0].gateways || []).filter(function (g) {
        if (g.disabled) return false;
        // chipId 'cod' — same convention as the codGateway lookup just
        // below, which already keys off this exact string.
        if (isGift && g.chipId === 'cod') return false;
        if (allowedChipIds && allowedChipIds.indexOf(g.chipId) === -1) return false;
        return true;
      });
      if (stale) return;
      var profile = results[1] ? results[1].profile : null;
      container.innerHTML = '';

      var chipsHost = window.ErosUI.el('div', 'eros-payment-chips');
      var hintHost = window.ErosUI.el('p', 'text-subdued eros-payment-hint');
      container.appendChild(chipsHost);
      container.appendChild(hintHost);

      // COD default if present, else first gateway — matches payment_gateway_mapper.dart.
      var codGateway = gateways.filter(function (g) { return g.chipId === 'cod'; })[0];
      var preferred = preferredGatewayName
        ? gateways.filter(function (g) { return g.name === preferredGatewayName; })[0]
        : null;
      var defaultGateway = preferred || codGateway || gateways[0];
      if (defaultGateway) selectedGatewayName = defaultGateway.name;

      gateways.forEach(function (gw) {
        var chip = window.ErosUI.el('button', 'eros-payment-chip', { type: 'button' });
        chip.textContent = gw.name;
        chip.setAttribute('aria-pressed', gw.name === selectedGatewayName ? 'true' : 'false');
        chip.addEventListener('click', function () {
          selectedGatewayName = gw.name;
          Array.prototype.forEach.call(chipsHost.children, function (c) {
            c.setAttribute('aria-pressed', 'false');
          });
          chip.setAttribute('aria-pressed', 'true');
          updateHint(gw);
          onChange();
        });
        chipsHost.appendChild(chip);
      });

      if (defaultGateway) updateHint(defaultGateway);

      function updateHint(gw) {
        hintHost.textContent = gw.instructionsAr || gw.instructionsEn || '';
      }

      if (walletHost && profile && profile.walletBalanceIqd) {
        var preview = previewWalletDue(profile.walletBalanceIqd, merchandiseTotal, shippingIqd);
        var isDebt = profile.walletBalanceIqd < 0;
        var wallet = window.ErosUI.el('div', 'eros-wallet-due');
        var balanceRow = window.ErosUI.el('p', 'eros-wallet-due__balance');
        balanceRow.textContent = (isDebt ? t('wallet_debt', 'مستحق سابق') : t('wallet_balance', 'رصيد المحفظة')) + ': ' + window.ErosUI.formatMoneyIqd(Math.abs(profile.walletBalanceIqd));
        var dueRow = window.ErosUI.el('p', 'bold');
        dueRow.textContent = t('amount_due', 'المطلوب دفعه') + ': ' + window.ErosUI.formatMoneyIqd(preview.amountDueIqd);
        wallet.appendChild(balanceRow);
        wallet.appendChild(dueRow);
        // This block re-runs whenever the gateways re-render (profile load,
        // reopen, fee refresh) — appending stacked the same two rows again
        // and again (owner report 2026-09-07). Replace, never append.
        walletHost.replaceChildren(wallet);
      }

      onChange();
    });

    return {
      getSelectedGatewayName: function () {
        return selectedGatewayName;
      },
    };
  }

  window.ErosCheckoutPayment = { mount: mount, previewWalletDue: previewWalletDue };
})();
